import { getDevBearerOverride } from "@/lib/dev-bearer";
import { GINJA_PLATFORM_API_BASE, SSO_COOKIE_SENTINEL } from "@/services/ginjaAuth";

const BRAND_STORAGE_KEY = "ginja-brand-settings";

/** Tab title — not tenant `brand_name` (e.g. parent org in Ginja). */
const APP_DOCUMENT_TITLE = "Assisted Onboarding";

export const GINJA_BRAND_UPDATED_EVENT = "ginja-brand-updated";

/** Nested colors from API — stored for reference; not applied to CSS variables. */
export interface GinjaBrandColors {
  primary?: string;
  secondary?: string;
  accent?: string;
}

/** Normalized `/brand-settings` `data` object (see platform API). */
export interface GinjaBrandSettingsData {
  brand_name?: string;
  brand_tagline?: string;
  logo_url?: string;
  favicon_url?: string;
  colors?: GinjaBrandColors;
  default_timezone?: string;
  default_currency?: string;
  default_language?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface GinjaBrandSettingsApiResponse {
  status?: string;
  message?: string;
  data?: GinjaBrandSettingsData;
  timestamp?: string;
}

/** Bearer for platform APIs: dev override, then `token`, then non-sentinel `auth-token`. */
export function bearerForGinjaApis(): string | null {
  const dev = getDevBearerOverride();
  if (dev) return dev;
  const t = localStorage.getItem("token");
  if (t && t !== SSO_COOKIE_SENTINEL) return t;
  const a = localStorage.getItem("auth-token");
  if (a && a !== SSO_COOKIE_SENTINEL) return a;
  return null;
}

/** Shallow clone API `data` for storage/UI — keeps `colors` and any extra keys the API adds. */
export function normalizeBrandSettingsData(raw: unknown): GinjaBrandSettingsData {
  if (!raw || typeof raw !== "object") return {};
  const o = { ...(raw as Record<string, unknown>) } as GinjaBrandSettingsData;
  const trimStr = (k: keyof GinjaBrandSettingsData) => {
    const v = o[k];
    if (typeof v === "string") {
      const t = v.trim();
      (o as Record<string, unknown>)[k as string] = t.length ? t : undefined;
    }
  };
  trimStr("brand_name");
  trimStr("brand_tagline");
  trimStr("logo_url");
  trimStr("favicon_url");
  trimStr("default_timezone");
  trimStr("default_currency");
  trimStr("default_language");
  trimStr("updated_at");
  if (o.colors && typeof o.colors === "object") {
    o.colors = { ...(o.colors as object) } as GinjaBrandColors;
  }
  return o;
}

export async function fetchBrandSettings(): Promise<GinjaBrandSettingsData | null> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const bearer = bearerForGinjaApis();
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  const res = await fetch(`${GINJA_PLATFORM_API_BASE}/brand-settings`, {
    method: "GET",
    credentials: "include",
    headers,
  });
  if (!res.ok) return null;
  const body = (await res.json()) as GinjaBrandSettingsApiResponse;
  const raw = body?.data;
  if (!raw) return null;
  return normalizeBrandSettingsData(raw);
}

/**
 * Persists brand for in-app UI (logo, etc.) and notifies listeners.
 * Does **not** set `document.title` or the tab favicon from tenant data — those stay **Assisted Onboarding** / the app icon from `index.html`.
 * Does **not** map `colors` to CSS variables (per product requirement).
 */
function resetDocumentFaviconToAppDefault(): void {
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]')
    .forEach((link) => {
      link.href = "/vite.svg";
      link.type = "image/svg+xml";
    });
}

export function applyBrandSettings(data: GinjaBrandSettingsData): void {
  document.title = APP_DOCUMENT_TITLE;
  resetDocumentFaviconToAppDefault();

  try {
    localStorage.setItem(BRAND_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent<GinjaBrandSettingsData>(GINJA_BRAND_UPDATED_EVENT, { detail: data }),
  );
}

export function applyCachedBrandSettings(): void {
  try {
    const raw = localStorage.getItem(BRAND_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    const data = normalizeBrandSettingsData(parsed);
    applyBrandSettings(data);
  } catch {
    /* ignore */
  }
}

export function resetBrandToDefaults(): void {
  document.title = APP_DOCUMENT_TITLE;
  try {
    localStorage.removeItem(BRAND_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  document.documentElement.style.removeProperty("--primary");
  document.documentElement.style.removeProperty("--secondary");
  document.documentElement.style.removeProperty("--accent");
  resetDocumentFaviconToAppDefault();
  window.dispatchEvent(new CustomEvent(GINJA_BRAND_UPDATED_EVENT));
}

export function readCachedBrandSettings(): GinjaBrandSettingsData | null {
  try {
    const raw = localStorage.getItem(BRAND_STORAGE_KEY);
    if (!raw) return null;
    return normalizeBrandSettingsData(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}
